package com.example.mystybag.appCore.views.addMerchantLocation

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import androidx.compose.runtime.withFrameNanos
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.layout.LayoutCoordinates
import androidx.compose.ui.layout.boundsInWindow
import androidx.compose.ui.layout.onGloballyPositioned
import com.example.mystybag.common.resources.SR
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.core.expected.LogHelper
import com.example.mystybag.core.expected.ensureGoogleSansFontFacesForDom
import com.example.mystybag.core.expected.googleSansFontFamilyCss
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.views.AppTheme
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.events.Event
import kotlin.js.jsTypeOf

private const val DEFAULT_LAT = 43.2141
private const val DEFAULT_LNG = 27.9147
private const val INITIAL_ZOOM = 15

// IMPORTANT: a *separate* container ID from the home-screen `LocationsMapView`
// (`mystybag-map-container`) so the two overlays never fight over the same
// DOM node. The map script tag below is shared (same `id` as
// LocationsMapView's), but the rendered map containers are distinct.
//
// We use a TWO-DIV structure on purpose:
//   * `MAP_CONTAINER_ID` — the OUTER, positioned host. We own its style;
//     `position: fixed` + bounds-driven left/top/width/height.
//   * `MAP_INNER_ID` — the INNER element passed to `new google.maps.Map(...)`.
//     The Maps SDK aggressively rewrites this element's inline style (it
//     calls `el.style.cssText = ...` / sets `position: relative` directly,
//     which silently strips our `!important` flag), so we sacrifice it as
//     a "throwaway" host. The outer container is what guarantees the
//     positioning never gets clobbered.
private const val MAP_CONTAINER_ID = "mystybag-merchant-location-map-container"
private const val MAP_INNER_ID = "mystybag-merchant-location-map-inner"
private const val SEARCH_WRAPPER_ID = "mystybag-merchant-location-search-wrap"
private const val SEARCH_INPUT_ID = "mystybag-merchant-location-search-input"
private const val SEARCH_CLEAR_BTN_ID = "mystybag-merchant-location-search-clear"
private const val MAPS_SCRIPT_ID = "mystybag-google-maps-script"
private const val MAPS_INIT_CALLBACK = "mystybagMapsInit"

private const val OVERLAY_Z_TOP = "2147483647"
private const val OVERLAY_Z_BEHIND_MASK = "0"

// Match the Compose `RoundedCornerShape(topStart = p20, topEnd = p20)` on the
// caller's modifier so the overlay clips identically to what the surrounding
// layout expects. Bottom corners are square because the address field is
// glued directly underneath in the design.
private const val CONTAINER_BORDER_RADIUS = "20px 20px 0 0"

// Module-level state. Singletons across (re)compositions and across full
// composable disposes — when the user collapses the accordion, the map
// instance and DOM container survive so re-expanding is instant. Everything
// here is created lazily inside the composable, never at file load.
private var currentMapInstance: dynamic = null
private var currentMarker: dynamic = null
private var merchantAutocompleteAttached = false
private var pacStackingStyleInjected = false

// Latest "click / drag" sink. Reset to null when the composable disposes so
// that stale lambdas can't fire into a torn-down ViewModel.
private var merchantOnLocationChanged: ((Double, Double) -> Unit)? = null

/**
 * Plain reference holder for the slot's latest [LayoutCoordinates]. Lives
 * inside `remember { ... }` so it survives recompositions but does NOT
 * inherit Compose's snapshot semantics — writing to it is free of
 * recomposition cost. We only need it as a place to stash the coords so
 * the per-frame poll can read them back.
 */
private class CoordsHolder {
    var coords: LayoutCoordinates? = null
}

/**
 * Web implementation of the merchant location-picker map.
 *
 * Strategy: the *interactive* Google Maps JavaScript SDK is mounted in a
 * `position: fixed` `<div>` overlay on top of the Compose `<canvas>`. To
 * avoid the previous overlay's brittle viewport-based geometry, we let the
 * Compose layout drive the position:
 *
 *   1. The composable reserves its layout slot via `Box(modifier
 *      .fillMaxSize().onGloballyPositioned)` and reports `boundsInWindow()`.
 *   2. On every bounds change (scroll inside the form, accordion
 *      expand/collapse, viewport resize, …) we re-read the canvas's
 *      `getBoundingClientRect()` and snap the overlay's CSS rect to
 *      `canvas.left + bounds.left`, `canvas.top + bounds.top`,
 *      `bounds.width`, `bounds.height`.
 *   3. Modal/dialog occlusion is honored via the existing
 *      `__mystybagBottomSheetOpen` / `__mystybagDialogOpen` window flags —
 *      same mechanism that `LocationsMapView` and `views.kt` already use.
 *
 * Native Android/iOS continue to use their own `MapView`s; this file is
 * jsMain-only.
 */
@Composable
actual fun MerchantRegistrationReviewStoreDetailsMapView(
    modifier: Modifier,
    latitude: Double?,
    longitude: Double?,
    onLocationChanged: (Double, Double) -> Unit,
    onMapReady: () -> Unit,
    searchPlaceHolder: String
) {
    val apiKey = remember { (window.asDynamic().MystyBagGoogleMapsApiKey as? String).orEmpty() }

    if (apiKey.isEmpty()) {
        Box(
            modifier = modifier
                .fillMaxSize()
                .background(AppTheme.current.colors.surfaceVariant),
            contentAlignment = Alignment.Center
        ) {
            Texts.BodyMedium(
                text = sharedString(SR.strings.map_web_placeholder),
                color = AppTheme.current.colors.onSurfaceVariant
            )
        }
        return
    }

    // Defaults are only used until the parent supplies real coordinates; the
    // map will recenter as soon as `latitude`/`longitude` become non-null.
    val centerLat = latitude ?: DEFAULT_LAT
    val centerLng = longitude ?: DEFAULT_LNG

    // Keep the latest callbacks reachable to the JS click/drag handlers
    // without re-attaching listeners on every recomposition.
    val onLocationChangedRef = rememberUpdatedState(onLocationChanged)
    val onMapReadyRef = rememberUpdatedState(onMapReady)
    val searchPlaceholderRef = rememberUpdatedState(searchPlaceHolder)

    // `coordsHolder` is the source of truth — it lets us re-query the slot's
    // current bounds even when `onGloballyPositioned` doesn't fire. It's a
    // plain mutable holder (NOT MutableState) so writing to it does not
    // trigger recomposition; we only care about reading the latest coords
    // each frame from the per-frame poll below.
    //
    // Why the per-frame poll? Compose Multiplatform's `verticalScroll`
    // translates its content via a graphicsLayer offset, and that
    // translation does NOT always invalidate `onGloballyPositioned` (it
    // does on layout pass, but not on every scroll-induced re-placement).
    // Without the poll, the overlay stays "stuck" at the slot's pre-scroll
    // coordinates and ends up visually covering siblings (Menu / Allergens
    // accordions) that have scrolled into the slot's old screen rect.
    val coordsHolder = remember { CoordsHolder() }
    var bounds by remember { mutableStateOf<Rect?>(null) }

    SideEffect {
        val inner = document.getElementById(MAP_INNER_ID) as? HTMLElement
        val outer = inner?.parentElement as? HTMLElement ?: return@SideEffect
        ensureMerchantSearchOverlay(outer, searchPlaceHolder)
    }

    DisposableEffect(apiKey) {
        val mapInner = ensureMerchantMapContainer(searchPlaceholderRef.value)
        applyMerchantMapLayerState()

        // The JS click / dragend listeners read from this var, so updating
        // `rememberUpdatedState` is enough to stay current — no listener
        // re-registration needed.
        merchantOnLocationChanged = { lat, lng -> onLocationChangedRef.value(lat, lng) }

        loadMerchantMapsScript {
            ensureMerchantMap(
                mapInner = mapInner,
                initialLat = centerLat,
                initialLng = centerLng
            )
            onMapReadyRef.value()
        }

        // Anything that can move the canvas relative to the viewport (window
        // resize, page-level scroll if the host ever introduces one) must
        // trigger a re-snap. Compose-internal scroll is handled by the
        // per-frame poll below. Capture phase is used so we hear scroll
        // events even if some inner element decides to stop them.
        val onWindowChange: (Event) -> Unit = { _: Event ->
            val coords = coordsHolder.coords
            val freshBounds = if (coords != null && coords.isAttached) {
                coords.boundsInWindow()
            } else {
                null
            }
            repositionMerchantMap(freshBounds)
        }
        window.addEventListener("resize", onWindowChange)
        window.addEventListener("scroll", onWindowChange, /* useCapture = */ true)

        onDispose {
            window.removeEventListener("resize", onWindowChange)
            window.removeEventListener("scroll", onWindowChange, /* useCapture = */ true)
            merchantOnLocationChanged = null
            // Hide but DON'T destroy: re-mount (re-expanding the accordion)
            // can immediately re-show the same map instance, and tearing
            // down `google.maps.Map` is an expensive WebGL/raster cleanup
            // that we'd rather amortize across the page lifetime.
            hideMerchantMap()
        }
    }

    // Per-frame poll: read the current LayoutCoordinates each frame and
    // reposition the overlay only when the bounds actually change. This is
    // robust to the Compose-Web `verticalScroll` translation issue noted
    // above and is cheap (a single `getBoundingClientRect` + 4 style
    // assignments when the bounds change, otherwise it's just a Rect
    // comparison).
    LaunchedEffect(Unit) {
        var lastBounds: Rect? = null
        while (true) {
            withFrameNanos { /* yield until next frame */ }
            val coords = coordsHolder.coords
            val newBounds = if (coords != null && coords.isAttached) {
                coords.boundsInWindow()
            } else {
                null
            }
            if (newBounds != lastBounds) {
                lastBounds = newBounds
                bounds = newBounds
                repositionMerchantMap(newBounds)
            }
        }
    }

    // Sync the marker's geographic position from props. We deliberately do
    // NOT call `map.panTo(...)` here — auto-centering on every external
    // change would yank the camera away after the user has manually panned.
    // Click-driven re-centering happens inside the JS click handler.
    LaunchedEffect(centerLat, centerLng) {
        updateMerchantMarker(centerLat, centerLng)
    }

    // Compose just reserves the layout slot. The Skia canvas will paint a
    // surfaceVariant rectangle here for the brief window before the DOM
    // overlay snaps into place; the overlay then covers it pixel-for-pixel.
    //
    // We keep `onGloballyPositioned` for fast initial placement (fires
    // synchronously on the first layout pass, before the per-frame poll
    // gets its first tick) and for non-scroll layout changes. The per-frame
    // poll handles scroll.
    Box(
        modifier = modifier
            .fillMaxSize()
            .background(AppTheme.current.colors.surfaceVariant)
            .onGloballyPositioned { coords ->
                coordsHolder.coords = coords
                val newBounds = coords.boundsInWindow()
                if (newBounds != bounds) {
                    bounds = newBounds
                    repositionMerchantMap(newBounds)
                }
            }
    )
}

// ---------------------------------------------------------------------------
// DOM overlay container
// ---------------------------------------------------------------------------

/**
 * Returns the inner `<div>` that should be passed to
 * `new google.maps.Map(...)`. Side-effect: lazily creates both the outer
 * positioning host and the inner map host on first call.
 *
 * Why two divs? See [MAP_CONTAINER_ID] doc. TL;DR — Maps SDK overwrites
 * inline styles in a way that bypasses `!important`, so we hand it a
 * sacrificial inner element instead of the one we use for positioning.
 */
private fun ensureMerchantMapContainer(searchPlaceholder: String): HTMLElement {
    val existingInner = document.getElementById(MAP_INNER_ID) as? HTMLElement
    if (existingInner != null) {
        (existingInner.parentElement as? HTMLElement)?.let { outer ->
            ensureMerchantSearchOverlay(outer, searchPlaceholder)
        }
        return existingInner
    }

    val parent = document.getElementById("root") as? HTMLElement
        ?: document.body as? HTMLElement
        ?: error("Document body not yet ready when creating merchant map container")

    val outer = (document.createElement("div") as HTMLElement).apply {
        id = MAP_CONTAINER_ID
        val s = style
        // OUTER element styles. We set position/left/top/width/height every
        // frame (driven from Compose bounds) — only the static visual
        // properties live here. `!important` is mostly cosmetic on the outer
        // since nothing else writes to it, but we keep it for parity with
        // the home-map overlay style and to be defensive against future
        // libraries that might.
        s.setProperty("position", "fixed", "important")
        s.setProperty("left", "0px", "important")
        s.setProperty("top", "0px", "important")
        s.setProperty("width", "0px", "important")
        s.setProperty("height", "0px", "important")
        s.setProperty("border-radius", CONTAINER_BORDER_RADIUS, "important")
        s.setProperty("overflow", "hidden", "important")
        s.setProperty("display", "none", "important")
        s.setProperty("background", "#e8e8e8", "important")
        s.setProperty("pointer-events", "auto", "important")
        // New stacking context so the Maps SDK's internal absolutely-
        // positioned controls cannot escape this container.
        s.setProperty("isolation", "isolate", "important")
        s.setProperty("z-index", OVERLAY_Z_TOP, "important")
    }

    val inner = (document.createElement("div") as HTMLElement).apply {
        id = MAP_INNER_ID
        // Pre-set position so Maps doesn't have to (it will anyway).
        // These styles will be rewritten by the SDK; that's fine — they're
        // here just to provide a sane initial state before SDK init lands.
        val s = style
        s.setProperty("position", "absolute", "important")
        s.setProperty("inset", "0", "important")
        s.setProperty("width", "100%", "important")
        s.setProperty("height", "100%", "important")
    }

    outer.appendChild(inner)
    ensureMerchantSearchOverlay(outer, searchPlaceholder)
    parent.appendChild(outer)
    return inner
}

/**
 * Floating search field above the map; wired to Places Autocomplete in
 * [tryAttachMerchantPlacesAutocomplete] once the map instance exists.
 */
private fun ensureMerchantSearchOverlay(outer: HTMLElement, placeholder: String) {
    ensureGoogleSansFontFacesForDom()
    var wrap = document.getElementById(SEARCH_WRAPPER_ID) as? HTMLElement
    if (wrap == null) {
        wrap = (document.createElement("div") as HTMLElement).apply {
            id = SEARCH_WRAPPER_ID
            val ws = style
            ws.setProperty("position", "absolute", "important")
            ws.setProperty("top", "10px", "important")
            ws.setProperty("left", "12px", "important")
            ws.setProperty("right", "12px", "important")
            ws.setProperty("z-index", "10", "important")
            ws.setProperty("pointer-events", "auto", "important")
        }
        val input = (document.createElement("input") as HTMLInputElement).apply {
            id = SEARCH_INPUT_ID
            type = "text"
            autocomplete = "off"
            val ins = style
            ins.setProperty("width", "100%", "important")
            ins.setProperty("box-sizing", "border-box", "important")
            ins.setProperty("height", "44px", "important")
            ins.setProperty("border-radius", "22px", "important")
            ins.setProperty("border", "2px solid #E6E6E6", "important")
            ins.setProperty("padding-left", "14px", "important")
            ins.setProperty("padding-right", "38px", "important")
            ins.setProperty("font-size", "15px", "important")
            ins.setProperty("font-family", googleSansFontFamilyCss(), "important")
            ins.setProperty("outline", "none", "important")
            ins.setProperty("background", "#fff", "important")
            ins.setProperty("box-shadow", "0 2px 8px rgba(0,0,0,0.12)", "important")
        }
        wrap.appendChild(input)
        outer.appendChild(wrap)
    }
    (document.getElementById(SEARCH_INPUT_ID) as? HTMLInputElement)?.apply {
        this.placeholder = placeholder
        style.setProperty("font-family", googleSansFontFamilyCss(), "important")
        style.setProperty("padding-left", "14px", "important")
        style.setProperty("padding-right", "38px", "important")
    }
    installMerchantSearchClearControl()
}

private fun syncMerchantSearchClearVisibility() {
    val input = document.getElementById(SEARCH_INPUT_ID) as? HTMLInputElement ?: return
    val btn = document.getElementById(SEARCH_CLEAR_BTN_ID) as? HTMLButtonElement ?: return
    btn.style.setProperty(
        "display",
        if (input.value.isNotBlank()) "flex" else "none",
        "important"
    )
}

/**
 * Circular × at the end of the address field; clears text on tap.
 */
private fun installMerchantSearchClearControl() {
    val input = document.getElementById(SEARCH_INPUT_ID) as? HTMLInputElement ?: return
    val wrap = input.parentElement as? HTMLElement ?: return
    var btn = document.getElementById(SEARCH_CLEAR_BTN_ID) as? HTMLButtonElement
    if (btn == null) {
        btn = (document.createElement("button") as HTMLButtonElement).apply {
            id = SEARCH_CLEAR_BTN_ID
            type = "button"
            textContent = "\u00D7"
            setAttribute("aria-label", "Clear address")
            val bs = style
            bs.setProperty("position", "absolute", "important")
            bs.setProperty("right", "8px", "important")
            bs.setProperty("top", "50%", "important")
            bs.setProperty("transform", "translateY(-50%)", "important")
            bs.setProperty("width", "28px", "important")
            bs.setProperty("height", "28px", "important")
            bs.setProperty("border-radius", "50%", "important")
            bs.setProperty("border", "none", "important")
            bs.setProperty("padding", "0", "important")
            bs.setProperty("margin", "0", "important")
            bs.setProperty("cursor", "pointer", "important")
            bs.setProperty("background", "rgba(0,0,0,0.06)", "important")
            bs.setProperty("color", "#444444", "important")
            bs.setProperty("font-family", googleSansFontFamilyCss(), "important")
            bs.setProperty("font-size", "20px", "important")
            bs.setProperty("line-height", "1", "important")
            bs.setProperty("display", "none", "important")
            bs.setProperty("align-items", "center", "important")
            bs.setProperty("justify-content", "center", "important")
            bs.setProperty("z-index", "2", "important")
        }
        wrap.appendChild(btn)
    }
    btn.asDynamic().onclick = {
        input.value = ""
        syncMerchantSearchClearVisibility()
        input.focus()
    }
    if (input.getAttribute("data-mysty-clear-bound") != "true") {
        input.setAttribute("data-mysty-clear-bound", "true")
        input.addEventListener("input", { syncMerchantSearchClearVisibility() })
    }
    syncMerchantSearchClearVisibility()
}

/**
 * Snap the overlay container's CSS rect to the laid-out Compose bounds.
 *
 * Coordinate math: `boundsInWindow()` returns coordinates relative to the
 * Compose window (i.e. the Skia `<canvas>`). To translate into the absolute
 * viewport coordinates that `position: fixed` wants, we add the canvas's
 * `getBoundingClientRect().left/top`. The canvas itself is `position:
 * relative` and currently fills `#root`, but reading its rect each time is
 * cheap insurance against future layout changes (top bar, sidebar, etc).
 */
private fun repositionMerchantMap(bounds: Rect?) {
    val outer = document.getElementById(MAP_CONTAINER_ID) as? HTMLElement ?: return

    if (bounds == null || bounds.width <= 0f || bounds.height <= 0f) {
        outer.style.setProperty("display", "none", "important")
        return
    }

    val canvas = document.querySelector("#root canvas") as? HTMLElement
    val canvasRect = canvas?.getBoundingClientRect()
    val originX = canvasRect?.left ?: 0.0
    val originY = canvasRect?.top ?: 0.0

    // Compose Multiplatform Web reports `boundsInWindow()` in CSS pixels
    // already (Skiko handles DPR scaling internally), so we can pass them
    // straight into CSS without dividing by `devicePixelRatio`. toInt() is
    // fine — sub-pixel placement isn't visible and avoids snapping leaks.
    val left = (originX + bounds.left).toInt()
    val top = (originY + bounds.top).toInt()
    val width = bounds.width.toInt().coerceAtLeast(0)
    val height = bounds.height.toInt().coerceAtLeast(0)

    val s = outer.style
    s.setProperty("position", "fixed", "important")
    s.setProperty("left", "${left}px", "important")
    s.setProperty("top", "${top}px", "important")
    s.setProperty("width", "${width}px", "important")
    s.setProperty("height", "${height}px", "important")
    s.setProperty("display", "block", "important")

    applyMerchantMapLayerState()

    // The Maps SDK caches its container size; explicitly fire `resize` so
    // tiles redraw to fit the (potentially) new dimensions. Cheap no-op when
    // the size hasn't changed.
    val map = currentMapInstance
    val maps = window.asDynamic().google?.maps
    if (map != null && maps != null) {
        try {
            maps.event.trigger(map, "resize")
        } catch (_: Throwable) {
            // Maps SDK occasionally throws during teardown races; ignored.
        }
    }
}

private fun hideMerchantMap() {
    val outer = document.getElementById(MAP_CONTAINER_ID) as? HTMLElement ?: return
    outer.style.setProperty("display", "none", "important")
}

/**
 * Honors the existing global occlusion signals used by `LocationsMapView` so
 * any open bottom sheet / dialog hides the merchant map underneath instead
 * of leaking through with a max z-index. This is updated on every reposition
 * AND when the modal flags flip (the latter is driven from `views.kt`).
 *
 * Always targets the OUTER container — the inner map host is a
 * Maps-SDK-controlled element whose styles we don't trust.
 */
private fun applyMerchantMapLayerState() {
    val outer = document.getElementById(MAP_CONTAINER_ID) as? HTMLElement ?: return
    val overlayBlocked = (window.asDynamic().__mystybagBottomSheetOpen as? Boolean == true) ||
        (window.asDynamic().__mystybagDialogOpen as? Boolean == true)
    outer.style.setProperty(
        "z-index",
        if (overlayBlocked) OVERLAY_Z_BEHIND_MASK else OVERLAY_Z_TOP,
        "important"
    )
    outer.style.setProperty(
        "pointer-events",
        if (overlayBlocked) "none" else "auto"
    )
}

// ---------------------------------------------------------------------------
// Maps SDK lifecycle
// ---------------------------------------------------------------------------

private fun ensureMerchantMap(
    mapInner: HTMLElement,
    initialLat: Double,
    initialLng: Double
) {
    if (!hasGoogleMapsApi()) return
    val maps = window.asDynamic().google.maps

    if (currentMapInstance != null) {
        // Map already exists from a previous mount. Just sync to the new
        // initial coords (e.g. the user might have geocoded a different
        // address while the accordion was collapsed).
        val pos = js("({})")
        pos.lat = initialLat
        pos.lng = initialLng
        try {
            currentMapInstance.setCenter(pos)
            currentMarker?.setPosition(pos)
            maps.event.trigger(currentMapInstance, "resize")
        } catch (e: Throwable) {
            LogHelper.logEvent("[MerchantMapView] resync failed: ${e.message}")
        }
        scheduleMerchantAutocompleteAttach(currentMapInstance, currentMarker)
        return
    }

    val options = js("({})")
    options.center = js("({})")
    options.center.lat = initialLat
    options.center.lng = initialLng
    options.zoom = INITIAL_ZOOM
    options.disableDefaultUI = false
    options.zoomControl = true
    options.mapTypeControl = false
    options.streetViewControl = false
    options.fullscreenControl = false
    // "greedy" lets a single-finger drag pan the map without requiring the
    // user to hold ctrl / use two fingers — desktop users expect this.
    options.gestureHandling = "greedy"
    // POIs that re-center on click would conflict with our "tap to pin"
    // semantics; disable them.
    options.clickableIcons = false

    try {
        // `mapInner` (NOT the outer container) is sacrificed to the Maps
        // SDK — see the doc on `MAP_CONTAINER_ID` for why.
        val map = js("new google.maps.Map(mapInner, options)").unsafeCast<dynamic>()
        currentMapInstance = map

        val markerOptions = js("({})")
        markerOptions.position = options.center
        markerOptions.map = map
        markerOptions.draggable = true
        val marker = js("new google.maps.Marker(markerOptions)").unsafeCast<dynamic>()
        currentMarker = marker

        map.addListener("click") { event ->
            try {
                val lat = (event.latLng.lat() as Double)
                val lng = (event.latLng.lng() as Double)
                marker.setPosition(event.latLng)
                map.panTo(event.latLng)
                merchantOnLocationChanged?.invoke(lat, lng)
            } catch (e: Throwable) {
                LogHelper.logEvent("[MerchantMapView] click handler failed: ${e.message}")
            }
        }

        marker.addListener("dragend") { event ->
            try {
                val lat = (event.latLng.lat() as Double)
                val lng = (event.latLng.lng() as Double)
                // No panTo — the marker is already at the user's chosen
                // spot relative to the map, and panning would yank the view.
                merchantOnLocationChanged?.invoke(lat, lng)
            } catch (e: Throwable) {
                LogHelper.logEvent("[MerchantMapView] dragend handler failed: ${e.message}")
            }
        }

        scheduleMerchantAutocompleteAttach(map, marker)
    } catch (e: Throwable) {
        LogHelper.logEvent("[MerchantMapView] Map construction failed: ${e.message}")
    }
}

/**
 * With `loading=async`, [hasGoogleMapsApi] often becomes true before
 * `google.maps.places` finishes registering — retry until Autocomplete exists.
 */
private fun scheduleMerchantAutocompleteAttach(map: dynamic, marker: dynamic?) {
    if (marker == null || merchantAutocompleteAttached) return
    var attempts = 0
    fun tick() {
        if (merchantAutocompleteAttached) return
        attempts++
        if (attempts > 120) {
            LogHelper.logEvent("[MerchantMapView] Autocomplete: places library or input not ready in time")
            return
        }
        if (document.getElementById(SEARCH_INPUT_ID) == null ||
            !hasGooglePlacesAutocomplete()
        ) {
            window.setTimeout({ tick() }, 50)
            return
        }
        tryAttachMerchantPlacesAutocomplete(map, marker)
        if (!merchantAutocompleteAttached) {
            window.setTimeout({ tick() }, 50)
        }
    }
    window.setTimeout({ tick() }, 0)
}

/** Suggestions render in `.pac-container` on `body`; map overlay uses max z-index, so pac must match or exceed it. */
private fun injectPacContainerAboveMapOverlays() {
    if (pacStackingStyleInjected) return
    pacStackingStyleInjected = true
    ensureGoogleSansFontFacesForDom()
    val font = googleSansFontFamilyCss()
    val style = document.createElement("style")
    style.id = "mystybag-pac-container-zfix"
    style.textContent =
        ".pac-container { z-index: 2147483647 !important; font-family: $font !important; }\n" +
            ".pac-item, .pac-item-query, .pac-matched { font-family: $font !important; }\n"
    document.head?.appendChild(style)
}

private fun hasGooglePlacesAutocomplete(): Boolean = js(
    "typeof window !== 'undefined' && " +
        "typeof window.google !== 'undefined' && " +
        "typeof window.google.maps !== 'undefined' && " +
        "typeof window.google.maps.places !== 'undefined' && " +
        "typeof window.google.maps.places.Autocomplete !== 'undefined'"
).unsafeCast<Boolean>()

/** One-shot: Google Maps JS has no built-in search UI; this uses Places Autocomplete. */
private fun tryAttachMerchantPlacesAutocomplete(map: dynamic, marker: dynamic?) {
    if (merchantAutocompleteAttached || marker == null) return
    if (!hasGooglePlacesAutocomplete()) return
    val input = document.getElementById(SEARCH_INPUT_ID) as? HTMLInputElement ?: return
    try {
        injectPacContainerAboveMapOverlays()
        val options = js("({})")
        options.fields = js("(['geometry', 'formatted_address', 'name'])")
        options.componentRestrictions = js("({ country: 'bg' })")
        val autocomplete = js("new google.maps.places.Autocomplete(input, options)").unsafeCast<dynamic>()
        merchantAutocompleteAttached = true
        autocomplete.addListener("place_changed") {
            try {
                val raw = autocomplete.getPlace().unsafeCast<dynamic>()
                val loc = raw.geometry?.unsafeCast<dynamic>()?.location
                if (loc != null) {
                    val lat = loc.lat() as Double
                    val lng = loc.lng() as Double
                    marker.setPosition(loc)
                    map.panTo(loc)
                    map.setZoom(INITIAL_ZOOM)
                    merchantOnLocationChanged?.invoke(lat, lng)
                }
            } catch (e: Throwable) {
                LogHelper.logEvent("[MerchantMapView] place_changed failed: ${e.message}")
            }
        }
    } catch (e: Throwable) {
        merchantAutocompleteAttached = false
        LogHelper.logEvent("[MerchantMapView] Autocomplete attach failed: ${e.message}")
    }
}

private fun updateMerchantMarker(lat: Double, lng: Double) {
    val marker = currentMarker ?: return
    try {
        val pos = js("({})")
        pos.lat = lat
        pos.lng = lng
        marker.setPosition(pos)
    } catch (e: Throwable) {
        LogHelper.logEvent("[MerchantMapView] updateMarker failed: ${e.message}")
    }
}

// ---------------------------------------------------------------------------
// Maps SDK script loading (cooperates with LocationsMapView)
// ---------------------------------------------------------------------------

private fun hasGoogleMapsApi(): Boolean = js(
    "typeof window !== 'undefined' && " +
        "typeof window.google !== 'undefined' && " +
        "typeof window.google.maps !== 'undefined' && " +
        "typeof window.google.maps.Map !== 'undefined'"
).unsafeCast<Boolean>()

/**
 * Loads the Google Maps JavaScript SDK exactly once across the whole app and
 * cooperates with [LocationsMapView]'s loader, which uses the same script
 * `id`:
 *
 *   - If the SDK is already loaded → invoke the callback synchronously.
 *   - If a script tag with our `id` already exists (added by either loader)
 *     → poll `hasGoogleMapsApi()` until it flips to true.
 *   - If no script tag exists → add one without a `callback=` query param,
 *     poll until ready, then opportunistically fire `window.mystybagMapsInit`
 *     so any pending callback registered by `LocationsMapView` doesn't get
 *     stranded.
 *
 * The polling interval is 50 ms with a 5 s timeout. Maps SDK normally loads
 * in < 500 ms, so this is well within budget.
 */
private fun loadMerchantMapsScript(callback: () -> Unit) {
    val apiKey = (window.asDynamic().MystyBagGoogleMapsApiKey as? String).orEmpty()
    if (apiKey.isEmpty()) return

    if (hasGoogleMapsApi()) {
        callback()
        return
    }

    if (document.getElementById(MAPS_SCRIPT_ID) == null) {
        val script = document.createElement("script")
        script.id = MAPS_SCRIPT_ID
        script.asDynamic().async = true
        script.asDynamic().defer = true
        script.asDynamic().src =
            "https://maps.googleapis.com/maps/api/js?key=$apiKey&loading=async&libraries=places"
        script.asDynamic().onerror = {
            LogHelper.logEvent("[MerchantMapView] Maps SDK script failed to load")
        }
        document.head?.appendChild(script)
    }

    val intervalIdHolder = intArrayOf(0)
    var attempts = 0
    intervalIdHolder[0] = window.setInterval({
        if (hasGoogleMapsApi()) {
            window.clearInterval(intervalIdHolder[0])
            // If `LocationsMapView` is also waiting for the SDK, its
            // `mystybagMapsInit` global is set up but won't fire because we
            // didn't pass a `callback=` URL param. Drain its pending list.
            val pending = window.asDynamic()[MAPS_INIT_CALLBACK]
            if (jsTypeOf(pending) == "function") {
                try {
                    pending()
                } catch (e: Throwable) {
                    LogHelper.logEvent("[MerchantMapView] Failed to drain shared maps callback: ${e.message}")
                }
            }
            callback()
        } else if (++attempts > 100) {
            window.clearInterval(intervalIdHolder[0])
            LogHelper.logEvent("[MerchantMapView] Maps SDK did not load within 5s, aborting")
        }
    }, 50)
}
